<script>
  import { tick, onMount } from "svelte";
  import { ButtonV2, CardV2, TextField, Snackbar } from "$lib";
  import { _ } from "$helpers/i18n.js";
  import { calculateProfitGain, calculateProfitRentability } from "../../../helpers/calculator.js";
  import { globalStore } from "../../store/globalStore";
  import { SUPPORTED_COUNTRIES } from '../../../../../constants/supportedCountries.js'
  import { PROFIT_EXAMPLE_STORAGE_KEY } from '../../../../../constants/storageKeys.js'

  const { country } = globalStore;
  const config = SUPPORTED_COUNTRIES[$country] || SUPPORTED_COUNTRIES['cl'];

  // Variables para los inputs
  let roughValue = null;
  let acquisitionCost = null;

  // Variable para el tipo de calculo seleccionado (por defecto 'rentability')
  let calculateType = "rentability";

  // Variable para el resultado
  let result = null;
  let resultType = null;

  $: roughValue, acquisitionCost, (result = null);

  let resultEl;

  onMount(async () => {
    if ($country === 'pe' && !localStorage.getItem(PROFIT_EXAMPLE_STORAGE_KEY)) {
      roughValue = config.marginRoughValue;
      acquisitionCost = config.marginAcquisitionCost;
      await tick(); // permite que la sentencia reactiva limpie el resultado antes de calcular
      calculate();
      localStorage.setItem(PROFIT_EXAMPLE_STORAGE_KEY, "true");
    }
  });

  // Variables para el Snackbar
  let showSnackbar = false;
  let snackbarMessage = ""; 

  // Realiza el cálculo
  async function calculate() {
    // Resetear snackbar
    showSnackbar = false;

    // Validación de valores positivos
    if (roughValue < 1 || acquisitionCost < 1) {
        snackbarMessage = $_("snackbar.commons.positive_values");
        showSnackbar = true;
      return;
    }

    // Calcula según el tipo seleccionado
    resultType = calculateType;
    if (calculateType == "percentage") {
      result = calculateProfitGain(roughValue, acquisitionCost).profitPercentage;
    } else if (calculateType == "rentability") {
      result = calculateProfitRentability(roughValue, acquisitionCost).rentability;
    }

    await tick();
    resultEl?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }
</script>

<CardV2 variant="outlined">
  <div on:keydown={(event) => event.key == '-' && event.preventDefault()}>
    <p>{$_("profit.calculation.first_input")}</p>
    <TextField
      placeholder="0"
      leadingText={$_("common.currency_symbol")}
      bind:value={roughValue}
      type="number"
      step="0.01"
      min={0}
      testId="profitPriceSaleInput"
    />
  </div>

  <div on:keydown={(event) => event.key == '-' && event.preventDefault()}>
    <p>{$_("profit.calculation.second_input")}</p>
    <TextField
      placeholder="0"
      leadingText={$_("common.currency_symbol")}
      bind:value={acquisitionCost}
      type="number"
      step="0.01"
      min={0}
      testId="profitAcquisitionCostInput"
    />
  </div>

  <div class="percentage-buttons">
    <ButtonV2
      variant="secondary"
      selected={calculateType == "rentability"}
      on:click={() => (calculateType = "rentability")}
    >
      {$_("profit.calculation.first_option")} <br /><strong>{$_("profit.calculation.second_option_bold")}</strong>
    </ButtonV2>

    <ButtonV2
      variant="secondary"
      selected={calculateType == "percentage"}
      on:click={() => (calculateType = "percentage")}
    >
      {$_("profit.calculation.first_option")} <br /><strong>{$_("profit.calculation.first_option_bold")}</strong>
    </ButtonV2>
  </div>

  <ButtonV2
    variant="primary"
    class="btn-principal"
    on:click={calculate}
    disabled={!roughValue || !acquisitionCost || !calculateType}
    dataTrack="calculate_profit"
  >
    {$_("common.calculate_button")}
  </ButtonV2>

  <!-- Campo de resultado -->
  {#if result != null}
    <div class="result-static" class:negative={result < 0} data-testid="profitCalculationResult" bind:this={resultEl}>
      <p>{$_(resultType == "percentage" ? "profit.calculation.result_percentage" : "profit.calculation.result_rentability")}</p>
      {`${result}%`}
    </div>
  {/if}

  <Snackbar 
    variant="warning" 
    visible={showSnackbar} 
    message={snackbarMessage} 
    on:close={() => (showSnackbar = false)}
  />
</CardV2>

